import { cytho, trans, value } from './provider'
import * as util from '../view-advice-util'

// ------------------------------------------
// Bewertung Text Generation
// ------------------------------------------

// Default
export default createKinetic
// Named export
export function createKinetic( dict, rule, marker, lang ) {

  if ( !rule || !rule.param.length ) {
    return dict.bew_unknown
  }

  // ----------------------------------------------------------
  // Extract specific pharmacokinetic values
  // see view-editor/data-rule-kinetic/run-transform/index.js
  // ----------------------------------------------------------
  const param = rule.param.reduce( ( acc, cur ) => {
    if ( /^bio po \d/.test( cur ) ) {
      acc.bio = value( "bio po", cur, "%" )
    }
    if ( /^t12 [<>]?\d+/.test( cur ) ) {
      acc.t12 = value( "t12", cur, "hr" )
    }
    if ( /^pb [<>]?\d+/.test( cur ) ) {
      acc.pb = value( "pb", cur, "%" )
    }
    if ( /^vd \d+/.test( cur ) ) {
      acc.vd = value( "vd", cur, "L" )
    }
    if ( /^nti true/.test( cur ) ) {
      acc.nti = true
    }
    if ( /^clpo [<>]?\d+/.test( cur ) ) {
      acc.clpo = value( "clpo", cur, "L/hr" )
    }
    if ( /^elim renal parent [<>]?\d/.test( cur ) ) {
      acc.feru = value( "elim renal parent", cur, "%" )
    }
    if ( /^path parent/.test( cur ) ) {
      const match = cur.match( /^path parent\s?([A-Z][A-Z0-9\-\_]+)?\s?([^\(\[]*)?/ )
      acc.cyp = cytho( acc.cyp, match )
      acc.tra = trans( acc.tra, match )
    }
    return acc
  }, { cyp: {}, tra: {} } )

  // ---------------------------------------------------
  // Rate the values
  // ---------------------------------------------------
  const texte = []

  // ----------------------------------------
  // Bioavailability
  // ----------------------------------------
  var bio = ""
  if ( param.bio > 0.8 ) {
    bio = dict.bew_f_high
  } else if ( param.bio <= 0.8 && param.bio > 0.4 ) {
    bio = dict.bew_f_medium
  } else if ( param.bio <= 0.4 ) {
    bio = dict.bew_f_low
  } else {
    bio = dict.bew_f_none
  }
  texte.push( bio.replace( "100%", `<b>${(param.bio*100).toFixed(0)}%</b>` ) )


  // ----------------------------------------
  // Half-life - IC 2020-09-20
  // ----------------------------------------
  var t12 = ""
  if ( param.t12 > 24 ) {
    t12 = dict.bew_t12_long
  }
  if ( param.t12 <= 24 && param.t12 > 6 ) {
    t12 = dict.bew_t12_normal
  }
  if ( param.t12 <= 6 ) {
    t12 = dict.bew_t12_short
  }

  // Make sure t12 is nice
  // Bug Fix IC 2021-11-10 toFixed is undefined
  // Only show sentence if we know t12
  if ( param.t12 ) {
    const t12Fixed = Math.round( param.t12 ) == param.t12 ? param.t12 : param.t12.toFixed( 1 )
    texte.push( t12.replace( "24", `<b>${t12Fixed}</b>` ).replace( "9999", `<b>${param.t12*4}</b>` ) )
  }

  // ----------------------------------------
  // NTI - ME 2021-07-17
  // ----------------------------------------
  var nti = ""
  if ( param.nti ) {
    nti = `${dict.bew_nti1} ${dict.bew_nti2}`
  }
  texte.push( nti )

  // ----------------------------------------
  // Protein binding - IC 2020-09-20
  // ----------------------------------------
  var pb = dict.bew_pb_unknown
  if ( param.pb > 0.98 ) {
    pb = dict.bew_pb_very_high
  }
  if ( param.pb <= 0.98 && param.pb > 0.95 ) {
    pb = dict.bew_pb_high
  }
  if ( param.pb <= 0.95 && param.pb > 0.7 ) {
    pb = dict.bew_pb_moderate
  }
  if ( param.pb <= 0.7 && param.pb > 0.3 ) {
    pb = dict.bew_pb_low
  }
  if ( param.pb <= 0.3 ) {
    pb = dict.bew_pb_very_low
  }
  // If no vd stop the sentence
  // Remove trailing zero
  if ( param.vd ) {
    texte.push( pb.replace( "100%", `<b>${(param.pb*100).toFixed(1).replace(/.0$/,"")}%</b>` ) )
  } else {
    texte.push( pb.replace( "100%", `<b>${(param.pb*100).toFixed(1).replace(/.0$/,"")}%</b>` ) + "." )
  }

  // ----------------------------------------------
  // Volume of distribution - ranges IC 2020-09-20
  // ----------------------------------------------
  var vd = ""
  // Limit 1L/kg
  if ( param.vd > 75 ) {
    vd = dict.bew_vd_very_high
  }
  // Limit 0.7 L/kg
  if ( param.vd <= 75 && param.vd > 52.5 ) {
    vd = dict.bew_vd_high
  }
  // Limit 0.3 - 0.7 L/kg
  if ( param.vd <= 52.5 && param.vd >= 23 ) {
    vd = dict.bew_vd_small
  }
  if ( param.vd < 23 ) {
    vd = dict.bew_vd_very_small
  }
  if ( vd ) {
    // if not available stop the sentence
    if ( param.bio && param.clpo && param.feru && param.feru <= 0.2 ) {
      texte.push( vd.replace( "100", `<b>${param.vd.toFixed(0)}</b>` ) + "," )
    } else {
      texte.push( vd.replace( "100", `<b>${param.vd.toFixed(0)}</b>` ) + "." )
    }
  }

  // ----------------------------------------
  // Calculate extraction ratio
  // ----------------------------------------
  var er = ""
  // Clh = Cliv x ( 1 - feru ) => ( Clpo x F ) x ( 1 - feru )
  if ( rule.data && rule.data.CLiv_blood && rule.data.Clr_blood ) {
    // Bad because considering plasma
    // var clh = param.bio * param.clpo * ( 1 - param.feru )
    var ratio = ( rule.data.CLiv_blood - rule.data.Clr_blood ) / 89
    if ( ratio > 0.7 ) {
      er = dict.bew_er_high
    }
    if ( ratio <= 0.7 && ratio > 0.3 ) {
      er = dict.bew_er_medium
    }
    if ( ratio <= 0.3 ) {
      er = dict.bew_er_low
    }
    texte.push( er.replace( "0.9", `<b>${ratio.toFixed(2)}</b>` ) )
  }

  // ----------------------------------------
  // Fraction renal excreted unchanged
  // ----------------------------------------
  if ( param.feru > 0.2 ) {
    var feru = ( param.feru * 100 ).toFixed( 1 )
    texte.push( dict.bew_feru.replace( "20%", `<b>${feru}%</b>` ) )
  }

  // -----------------------------------------
  // Prodrug
  // -----------------------------------------

  // ----------------------------------------------------
  // Metabolism: Default cyp black but if influence blue
  // ----------------------------------------------------
  // Cytochrome
  // console.log( rule.drug, rule.status )

  const cyps = Object.keys( param.cyp ).sort()
  var cyp = ""
  if ( rule.status.some( flag => flag == "path_valid" || flag == "valid" ) ) {
    cyp = dict.bew_cyp0
  } else {
    cyp = dict.bew_cyptodo
  }
  if ( cyps.length == 1 ) {
    cyp = dict.bew_cyp1
  } else if ( cyps.length > 1 ) {
    cyp = dict.bew_cyp2
  }
  cyp = util.replaceList( cyp, "CYP9X9", "CYP8X8", cyps.map( cyp => `<b>${cyp}</b>` ), lang )
  // Correct color of cyp if actual influenced
  marker.forEach( mark => {
    cyp = cyp.replace( `<b>${mark}</b>`, `<b class=b>${mark}</b>` )
  } );

  // Stop sentence if no transport
  if ( Object.keys( param.tra ).length == 0 ) {
    texte.push( cyp + "." )
  } else {
    texte.push( cyp )
  }

  const tras = Object.keys( param.tra ).sort()
  var tra = ""
  if ( tras.length == 1 ) {
    tra = dict.bew_tra1
  } else if ( tras.length > 1 ) {
    tra = dict.bew_tra2
  }
  tra = util.replaceList( tra, "TRA9X9", "TRA8X8", tras.map( tra => `<b>${tra}</b>` ), lang )
  // Correct color of tra if actual influenced
  marker.forEach( mark => {
    tra = tra.replace( `<b>${mark}</b>`, `<b class=b>${mark}</b>` )
  } );
  texte.push( tra )

  return texte.join( " " )
}